import React, { useRef, useState } from 'react';
import { Button, Modal, Slider, Upload, message } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import axios from 'axios';

const MAX_PROFILE_PICTURE_SIZE = 10 * 1024 * 1024;
const CROP_SIZE = 280;
const OUTPUT_SIZE = 512;

const getUploadErrorMessage = (error) => {
   const responseMessage = error?.response?.data?.message;

   if (responseMessage) return responseMessage;
   if (typeof error?.response?.data === 'string') return error.response.data;

   return 'Unable to upload the profile picture.';
};

const constrainPosition = (position, image, scale) => ({
   x: Math.min(0, Math.max(CROP_SIZE - (image.width * scale), position.x)),
   y: Math.min(0, Math.max(CROP_SIZE - (image.height * scale), position.y)),
});

export function ProfilePictureUpload({ userId, onUploaded }) {
   const [uploading, setUploading] = useState(false);
   const [cropFile, setCropFile] = useState(null);
   const [cropImageUrl, setCropImageUrl] = useState(null);
   const [cropImage, setCropImage] = useState(null);
   const [minimumScale, setMinimumScale] = useState(1);
   const [scale, setScale] = useState(1);
   const [position, setPosition] = useState({ x: 0, y: 0 });
   const dragStart = useRef(null);

   const closeCropper = () => {
      if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
      setCropFile(null);
      setCropImageUrl(null);
      setCropImage(null);
      dragStart.current = null;
   };

   const selectPicture = (file) => {
      if (!`${file.type || ''}`.toLowerCase().startsWith('image/')) {
         message.error('Please select an image file.');
         return Upload.LIST_IGNORE;
      }

      if (file.size > MAX_PROFILE_PICTURE_SIZE) {
         message.error('The profile picture must be 10 MB or smaller.');
         return Upload.LIST_IGNORE;
      }

      setCropFile(file);
      setCropImageUrl(URL.createObjectURL(file));
      return Upload.LIST_IGNORE;
   };

   const initialiseCrop = (event) => {
      const image = { width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight };
      const nextScale = Math.max(CROP_SIZE / image.width, CROP_SIZE / image.height);

      setCropImage(image);
      setMinimumScale(nextScale);
      setScale(nextScale);
      setPosition({
         x: (CROP_SIZE - (image.width * nextScale)) / 2,
         y: (CROP_SIZE - (image.height * nextScale)) / 2,
      });
   };

   const changeScale = (nextScale) => {
      setScale(nextScale);
      if (cropImage) setPosition((current) => constrainPosition(current, cropImage, nextScale));
   };

   const startDragging = (event) => {
      if (!cropImage || uploading) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragStart.current = { x: event.clientX, y: event.clientY, position };
   };

   const dragImage = (event) => {
      if (!dragStart.current || !cropImage) return;

      setPosition(constrainPosition({
         x: dragStart.current.position.x + event.clientX - dragStart.current.x,
         y: dragStart.current.position.y + event.clientY - dragStart.current.y,
      }, cropImage, scale));
   };

   const stopDragging = () => {
      dragStart.current = null;
   };

   const uploadPicture = async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('s3path', `userprofiles/${userId}`);

      const response = await axios.post(
         `${process.env.REACT_APP_API_S3_UPLOAD}/uploaduserpicture`,
         formData
      );
      const key = response.data?.key;

      if (!key) throw new Error('The upload completed without returning a file key.');

      return key;
   };

   const cropAndUpload = async () => {
      if (!cropFile || !cropImage) return;

      setUploading(true);

      try {
         const imageElement = new Image();
         imageElement.src = cropImageUrl;
         await new Promise((resolve, reject) => {
            imageElement.onload = resolve;
            imageElement.onerror = reject;
         });

         const canvas = document.createElement('canvas');
         canvas.width = OUTPUT_SIZE;
         canvas.height = OUTPUT_SIZE;
         const context = canvas.getContext('2d');
         const outputScale = OUTPUT_SIZE / CROP_SIZE;

         if (!context) throw new Error('Unable to prepare the image crop.');

         context.beginPath();
         context.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
         context.clip();
         context.drawImage(
            imageElement,
            position.x * outputScale,
            position.y * outputScale,
            cropImage.width * scale * outputScale,
            cropImage.height * scale * outputScale
         );

         // Every profile picture is stored as PNG, regardless of the source image format.
         const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
         if (!blob) throw new Error('Unable to create the cropped image.');

         const key = await uploadPicture(new File(
            [blob],
            `${userId}.png`,
            { type: 'image/png' }
         ));
         const previewUrl = canvas.toDataURL('image/png');
         await onUploaded(key, previewUrl);
         closeCropper();
         message.success('Profile picture updated.');
      } catch (error) {
         message.error(getUploadErrorMessage(error));
      } finally {
         setUploading(false);
      }
   };

   return (
      <>
         <Upload accept="image/*" beforeUpload={selectPicture} maxCount={1} showUploadList={false}>
            <Button icon={<CameraOutlined />} block loading={uploading} disabled={uploading}>
               Change Picture
            </Button>
         </Upload>

         <Modal
            className="profile-picture-crop-modal"
            title="Position Profile Picture"
            open={Boolean(cropImageUrl)}
            onCancel={closeCropper}
            onOk={cropAndUpload}
            okText="Crop & Upload"
            confirmLoading={uploading}
            cancelButtonProps={{ disabled: uploading }}
            destroyOnClose
            centered
         >
            <div className="profile-picture-cropper">
               <div
                  className="profile-picture-cropper-stage"
                  onPointerDown={startDragging}
                  onPointerMove={dragImage}
                  onPointerUp={stopDragging}
                  onPointerCancel={stopDragging}
               >
                  {cropImageUrl && (
                     <img
                        src={cropImageUrl}
                        alt="Crop preview"
                        draggable={false}
                        onLoad={initialiseCrop}
                        style={{
                           height: cropImage ? cropImage.height * scale : 'auto',
                           left: position.x,
                           top: position.y,
                           width: cropImage ? cropImage.width * scale : 'auto',
                        }}
                     />
                  )}
               </div>
               <p>Drag the image to position it inside the circle.</p>
               <Slider
                  min={minimumScale}
                  max={minimumScale * 3}
                  step={0.01}
                  value={scale}
                  onChange={changeScale}
                  disabled={!cropImage || uploading}
                  tooltipVisible={false}
               />
            </div>
         </Modal>
      </>
   );
}
